import { HumanizeDurationOptions } from "humanize-duration-ts";

import { DATE_FORMATS, DateTime, Duration } from "@bps/date-time";
import { LabeledText, Stack, Text, useTheme } from "@bps/fluent-ui";
import { RolloutPackageDetail } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { getHumanizedTimeRelativeToNow } from "@libs/common/common.utils";

interface DeploymentDetailTimelineTooltipProps {
  deploymentRingName: string;
  rolloutName: string;
  assignmentSource: string;
  ratio: number;
  initialInstallStartUtc: DateTime;
  initialInstallEndUtc: DateTime;
  initialDownloadStartUtc: DateTime;
  initialDownloadEndUtc: DateTime;
  packages: RolloutPackageDetail[];
}

export const DeploymentDetailTimelineTooltip: React.FC<DeploymentDetailTimelineTooltipProps> = ({
  deploymentRingName,
  rolloutName,
  assignmentSource,
  ratio,
  initialInstallStartUtc,
  initialInstallEndUtc,
  initialDownloadStartUtc,
  initialDownloadEndUtc,
  packages
}) => {
  const theme = useTheme();

  const humanizeOptions = {
    units: ["d"],
    round: true
  } as HumanizeDurationOptions;

  const futureHumanizeOptions = {
    units: ["d", "h", "m"],
    round: true
  } as HumanizeDurationOptions;

  const nowUtc = DateTime.now().setZone("UTC");
  const formatDate = (value: DateTime) =>
    value.toFormat(DATE_FORMATS.LONG_DATE_TIME_FORMAT);

  const isInFuture = nowUtc < initialInstallEndUtc;

  const downloadStartFormatted = formatDate(initialDownloadStartUtc);
  const downloadEndFormatted = formatDate(initialDownloadEndUtc);
  const downloadStartRelativeLabel = getHumanizedTimeRelativeToNow(
    initialDownloadStartUtc,
    nowUtc,
    humanizeOptions
  );

  const installStartFormatted = formatDate(initialInstallStartUtc);
  const installEndFormatted = formatDate(initialInstallEndUtc);
  const installStartRelativeLabel = getHumanizedTimeRelativeToNow(
    initialInstallStartUtc,
    nowUtc,
    humanizeOptions
  );

  const getRatioDate = (start: DateTime, end: DateTime) => {
    const diff = (end.valueOf() - start.valueOf()) * ratio;
    return DateTime.fromMillis(start + Duration.fromMillis(diff));
  };

  const actualDownloadStart = getRatioDate(
    initialDownloadStartUtc,
    initialDownloadEndUtc
  );

  const actualInstallStart = getRatioDate(
    initialInstallStartUtc,
    initialInstallEndUtc
  );

  const actualDownloadHumanized = getHumanizedTimeRelativeToNow(
    actualDownloadStart,
    nowUtc,
    futureHumanizeOptions
  );

  const actualInstallHumanized = getHumanizedTimeRelativeToNow(
    actualInstallStart,
    nowUtc,
    futureHumanizeOptions
  );

  const assignmentSourceLabel =
    assignmentSource === "Manual"
      ? "Manually assigned to this device"
      : "Assigned by Tenant";

  return (
    <Stack
      tokens={{ childrenGap: theme.spacing.s1, padding: theme.spacing.s1 }}
      styles={{ root: { pointerEvents: "none" } }}
    >
      <LabeledText label="Deployment Ring" wrap>
        <Stack>
          <Text>{deploymentRingName}</Text>
          <Text>({assignmentSourceLabel})</Text>
        </Stack>
      </LabeledText>
      <LabeledText label="Rollout" wrap>
        <Text>{rolloutName}</Text>
      </LabeledText>
      <LabeledText label="Download Window" wrap>
        <Stack>
          <Text>
            {downloadStartFormatted} to {downloadEndFormatted}
          </Text>
          <Text>({downloadStartRelativeLabel})</Text>
        </Stack>
      </LabeledText>
      <LabeledText label="Install Window" wrap>
        <Stack>
          <Text>
            {installStartFormatted} to {installEndFormatted}
          </Text>
          <Text>({installStartRelativeLabel})</Text>
        </Stack>
      </LabeledText>
      {isInFuture && (
        <LabeledText label="Device Dates" wrap>
          <Stack>
            <Text>Earliest download: {formatDate(actualDownloadStart)}</Text>
            <Text>({actualDownloadHumanized})</Text>
            <Text>Earliest install: {formatDate(actualInstallStart)}</Text>
            <Text>({actualInstallHumanized})</Text>
          </Stack>
        </LabeledText>
      )}
      <LabeledText label="Software Packages" wrap>
        {packages?.length > 0 ? (
          packages.map(pkg => {
            return (
              <Stack
                tokens={{ childrenGap: theme.spacing.s1 }}
                key={pkg.softwarePackageVersionId}
                horizontal
                horizontalAlign="space-between"
              >
                <Text>{pkg.softwarePackageName}</Text>
                <Text>v{pkg.version}</Text>
              </Stack>
            );
          })
        ) : (
          <Text>No packages available.</Text>
        )}
      </LabeledText>
    </Stack>
  );
};
